/**
 * Colors already used in a flow's rich text, offered as swatches under the
 * toolbar's color pickers so the same color can be reused across pages without
 * retyping its hex value.
 *
 * Works on serialized HTML (what the editor stores) rather than a DOM, so it
 * runs anywhere and needs no editor instance.
 */

export interface UsedColors {
  text: string[];
  highlight: string[];
}

/** Two rows of the picker's swatch grid. */
export const MAX_SWATCHES = 14;

/**
 * Every string inside `value` that looks like HTML. Walks node data generically
 * because rich text lives in more than one place (a page's `content`, each
 * RichText element's `content`, notes) and the shapes differ by node type.
 */
export function collectHtml(value: unknown, out: string[] = []): string[] {
  if (typeof value === 'string') {
    if (value.includes('<')) out.push(value);
  } else if (Array.isArray(value)) {
    value.forEach((item) => collectHtml(item, out));
  } else if (value && typeof value === 'object') {
    Object.values(value).forEach((item) => collectHtml(item, out));
  }
  return out;
}

/**
 * Lowercase hex for the forms the editor writes (#rgb, #rrggbb, rgb(), rgba()),
 * so the same color spelled two ways is offered once. Anything else (a named
 * color) is kept as written, lowercased.
 */
export function normalizeColor(raw: string): string | null {
  const value = raw.trim().toLowerCase();
  if (!value || value === 'inherit' || value === 'transparent' || value === 'currentcolor') return null;

  const shortHex = value.match(/^#([0-9a-f])([0-9a-f])([0-9a-f])$/);
  if (shortHex) return `#${shortHex[1]}${shortHex[1]}${shortHex[2]}${shortHex[2]}${shortHex[3]}${shortHex[3]}`;
  if (/^#[0-9a-f]{6}$/.test(value)) return value;

  const rgb = value.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+)\s*)?\)$/);
  if (rgb) {
    if (rgb[4] !== undefined && Number(rgb[4]) === 0) return null;
    return `#${[rgb[1], rgb[2], rgb[3]].map((n) => Math.min(255, Number(n)).toString(16).padStart(2, '0')).join('')}`;
  }
  return value;
}

function styleProperty(style: string, property: string): string | undefined {
  for (const declaration of style.split(';')) {
    const colon = declaration.indexOf(':');
    if (colon === -1) continue;
    if (declaration.slice(0, colon).trim().toLowerCase() === property) {
      return declaration.slice(colon + 1).trim();
    }
  }
  return undefined;
}

function attribute(tag: string, name: string): string | undefined {
  return tag.match(new RegExp(`\\s${name}\\s*=\\s*"([^"]*)"`, 'i'))?.[1];
}

/**
 * Text and highlight colors used across `htmlSources`, most used first. A
 * highlight is a <mark> (TipTap's Highlight extension); a text color is a
 * `color` style on any other element (TipTap's Color extension writes it on a
 * textStyle <span>).
 */
export function collectUsedColors(htmlSources: string[], limit = MAX_SWATCHES): UsedColors {
  const text = new Map<string, number>();
  const highlight = new Map<string, number>();
  const count = (map: Map<string, number>, raw: string | undefined) => {
    const color = raw ? normalizeColor(raw) : null;
    if (color) map.set(color, (map.get(color) ?? 0) + 1);
  };

  for (const html of htmlSources) {
    for (const [tag, name] of html.matchAll(/<([a-z][a-z0-9]*)\b[^>]*>/gi)) {
      const style = attribute(tag, 'style') ?? '';
      if (name.toLowerCase() === 'mark') {
        count(highlight, attribute(tag, 'data-color') ?? styleProperty(style, 'background-color'));
      } else {
        count(text, styleProperty(style, 'color'));
      }
    }
  }

  // Map iteration is insertion order, and sort is stable, so ties keep the
  // order the colors first appear in.
  const ranked = (map: Map<string, number>) =>
    [...map.entries()].sort((a, b) => b[1] - a[1]).slice(0, limit).map(([color]) => color);
  return { text: ranked(text), highlight: ranked(highlight) };
}
